import {
  ActionModal,
  Box,
  Card,
  CardContent,
  CardHeader,
  CloseIcon,
  DateTimeField,
  EllipsisVIcon,
  EnvelopeIcon,
  FormModal,
  HelperText,
  PencilAltIcon,
  SingleInfoCard,
  SpinnerButton,
  Typography,
  useModal,
  useSnackbar,
} from '@forgeglobal/design-framework'
import { CampaignEmailWarning } from 'components/campaigns/CampaignEmailWarning'
import { Form, FormRenderProps } from 'react-final-form'
import {
  OfferingDateDataFragment,
  OfferingEmailType,
  SpecificOfferingFragment,
  useAllOfferingDatesQuery,
  useGetOfferingQuery,
  useSendOfferingEmailsMutation,
  useUpdateOfferingEmailReminderMutation,
} from 'graphql/bindings/AdminBindings'
import { PopoverAction, PopoverListItem, PopoverListItemIcon, PopoverListItemText } from 'isolated-design-library'
import { isWithin72Hours } from 'components/campaigns/CampaignForm'
import React, { Fragment } from 'react'
import moment from 'moment'

interface FormShape {
  reminder_email_at?: string
  emailType?: OfferingEmailType
  startDate?: string
  endDate?: string
}

const ReminderEmailForm: React.FC<FormRenderProps<FormShape> & { otherOfferings: OfferingDateDataFragment[] }> = ({
  handleSubmit,
  values,
  otherOfferings,
}) => {
  const offeringReminderMoment = moment(values.reminder_email_at ?? '')
  const nearbyReminderOfferings = otherOfferings.filter(offering =>
    isWithin72Hours(offeringReminderMoment, moment(offering.auto_reminder_email_at))
  )
  return (
    <form onSubmit={handleSubmit}>
      {values.emailType === OfferingEmailType.Feedback ? (
        <DateTimeField
          name="reminder_email_at"
          minDate={values.endDate}
          minDateMessage="Email must be after offering end date"
          maxDate={moment(values.endDate).add(1, 'week')}
          maxDateMessage="Email must be within a week of the offering end date"
          label="Send at"
        />
      ) : (
        <DateTimeField
          name="reminder_email_at"
          minDate={values.startDate}
          minDateMessage="Email must be after offering start date"
          maxDate={values.endDate}
          maxDateMessage="Email must be before offering end date"
          label="Send at"
        />
      )}
      {nearbyReminderOfferings.length > 0 && (
        <HelperText>
          <CampaignEmailWarning
            content="Warning: Offerings reminders within a 72 hour window may be combined."
            dateType="invitation"
            otherOfferings={nearbyReminderOfferings}
          />
        </HelperText>
      )}
      <Box mt={2}>
        <SpinnerButton variant="contained" color="primary" type="submit" fullWidth loading={false} caption="Save" />
      </Box>
    </form>
  )
}

const validateEmailDate = (values: FormShape) => {
  let errors: { [key: string]: string } = {}

  if (!values.reminder_email_at) {
    errors.reminder_email_at = 'Please enter a valid date'
  }

  const startDate = moment(values.startDate)
  const endDate = moment(values.endDate)
  const reminderDate = moment(values.reminder_email_at)

  if (values.emailType === OfferingEmailType.Feedback) {
    if (reminderDate.isBefore(endDate)) {
      errors.reminder_email_at = 'Feedback date must be after offering end date'
    } else if (reminderDate.isAfter(endDate.add(1, 'week'))) {
      errors.reminder_email_at = 'Feedback date must be within a week of offering end date'
    }
  } else {
    if (reminderDate.isBefore(startDate)) {
      errors.reminder_email_at = 'Reminder date must be after offering start date'
    } else if (reminderDate.isAfter(endDate)) {
      errors.reminder_email_at = 'Reminder date must be before offering end date'
    }
  }

  return errors
}

const ReminderEmailModal: React.FC<{
  hideModal: () => void
  reminderTime: string
  offeringId?: string
  emailType: OfferingEmailType
  startDate: string
  endDate: string
  onSubmit: (values: FormShape) => void
}> = ({ hideModal, reminderTime, emailType, startDate, endDate, onSubmit, offeringId }) => {
  const { data: offeringDatesData } = useAllOfferingDatesQuery()
  const otherOfferings =
    emailType === OfferingEmailType.Reminder
      ? (offeringDatesData?.allOfferings.data ?? [])
          .filter(offering => offering.id !== offeringId)
          .sort((a, b) => (a.available_from > b.available_from ? 1 : -1))
      : []
  const initialTime =
    reminderTime ??
    (emailType === OfferingEmailType.Feedback
      ? moment(endDate)
          .add(1, 'day')
          .format()
      : moment(startDate)
          .add(2, 'days')
          .format())
  const initialValues: FormShape = {
    reminder_email_at: initialTime,
    emailType,
    startDate,
    endDate,
  }
  return (
    <FormModal open hideModal={hideModal} title="Edit: Email Schedule">
      <Form<FormShape>
        render={props => <ReminderEmailForm otherOfferings={otherOfferings} {...props} />}
        onSubmit={(values: FormShape) => {
          onSubmit(values)
          hideModal()
        }}
        validate={validateEmailDate}
        initialValues={{
          ...initialValues,
        }}
      />
    </FormModal>
  )
}

const CancelReminderEmailModal: React.FC<{ hideModal: () => void; onConfirm: () => void }> = props => {
  const { hideModal, onConfirm } = props

  return (
    <ActionModal
      open
      hideModal={hideModal}
      title="Cancel Reminder Email"
      cancelAction={hideModal}
      confirmAction={onConfirm}
      positiveMessage="Confirm"
      negativeMessage="Cancel"
      header="Are you sure?"
      actionMessage={
        'Automated email is an important part of the offering process. This will cancel a scheduled email. ' +
        'You can select a new time later if you choose.'
      }
    />
  )
}

const SendEmailConfirmModal: React.FC<{
  hideModal: () => void
  onConfirm: () => void
  offeringId?: string
}> = props => {
  const { hideModal, onConfirm, offeringId } = props
  const { data: offeringDatesData } = useAllOfferingDatesQuery()
  const allOfferings = offeringDatesData?.allOfferings.data ?? []
  const otherOfferings = allOfferings
    .filter(offering => offering.id !== offeringId)
    .sort((a, b) => (a.available_from > b.available_from ? 1 : -1))
  const nearbyReminderOfferings = otherOfferings.filter(offering =>
    isWithin72Hours(moment(), moment(offering.auto_reminder_email_at))
  )
  return (
    <ActionModal
      open
      hideModal={hideModal}
      title="Send Reminder Email"
      cancelAction={hideModal}
      confirmAction={onConfirm}
      positiveMessage="Confirm"
      negativeMessage="Cancel"
      header="Are you sure?"
      actionMessage={
        <Fragment>
          You are about to send a mass email to offering participants.
          {nearbyReminderOfferings.length > 0 && (
            <Typography variant="body2">
              <CampaignEmailWarning
                content="Offerings reminders within a 72 hour window may be combined."
                dateType="reminder"
                otherOfferings={nearbyReminderOfferings}
              />
            </Typography>
          )}
        </Fragment>
      }
    />
  )
}

const offeringEmailData = (emailType: OfferingEmailType, offering: SpecificOfferingFragment | undefined) => {
  switch (emailType) {
    case OfferingEmailType.Invitation:
      return {
        label: 'Invitation',
        scheduled: offering?.available_from,
        sent: offering?.previous_invitation_email_at,
      }
    case OfferingEmailType.Reminder:
      return {
        label: 'Reminder',
        scheduled: offering?.auto_reminder_email_at,
        sent: offering?.previous_reminder_email_at,
      }
    case OfferingEmailType.OrderReminder:
      return {
        label: 'Order Reminder',
        scheduled: offering?.auto_order_reminder_email_at,
        sent: offering?.previous_order_reminder_email_at,
      }
    case OfferingEmailType.Feedback:
      return {
        label: 'Feedback',
        scheduled: offering?.auto_feedback_email_at,
        sent: offering?.previous_feedback_email_at,
      }
  }
}

const statusFromTimes = (scheduled: string, sent: string) => {
  if (scheduled && sent && moment(scheduled) < moment(sent))
    return {
      statusLabel: 'Sent',
      time: sent,
    }
  else if (scheduled)
    return {
      statusLabel: 'Scheduled',
      time: scheduled,
    }
  else if (sent)
    return {
      statusLabel: 'Sent',
      time: sent,
    }
  return {
    statusLabel: 'Not Scheduled',
    time: '',
  }
}

const ReminderToInfoCard: React.FC<{ offeringId: string; emailType: OfferingEmailType }> = ({
  offeringId,
  emailType,
}) => {
  const { data, refetch } = useGetOfferingQuery({
    variables: { id: offeringId },
  })
  const offering = data?.offering ?? undefined
  const offeringStatus = offering?.status
  const { label, scheduled, sent } = offeringEmailData(emailType, offering)

  const [sendOfferingEmails] = useSendOfferingEmailsMutation()
  const [updateEmailReminder] = useUpdateOfferingEmailReminderMutation()
  const { enqueueSnackbar } = useSnackbar()

  const updateTime = (values: FormShape) => {
    updateEmailReminder({
      variables: {
        offeringId,
        emailType,
        sendReminderAt: values.reminder_email_at,
      },
    }).then(() => enqueueSnackbar('Email schedule saved'))
  }

  const cancelReminder = () => {
    updateEmailReminder({
      variables: {
        offeringId,
        emailType,
      },
    }).then(() => enqueueSnackbar('Scheduled email canceled'))
  }

  const [showDateTimeModal, hideDateTimeModal] = useModal(() => {
    return (
      <ReminderEmailModal
        hideModal={hideDateTimeModal}
        offeringId={offeringId}
        reminderTime={scheduled}
        emailType={emailType}
        startDate={offering?.available_from}
        endDate={offering?.available_to}
        onSubmit={updateTime}
      />
    )
  }, [scheduled, emailType, offering, updateTime])

  const [showSendConfirmModal, hideSendConfirmModal] = useModal(() => {
    return (
      <SendEmailConfirmModal
        offeringId={emailType === OfferingEmailType.Reminder ? offeringId : undefined}
        hideModal={hideSendConfirmModal}
        onConfirm={() => {
          sendOfferingEmails({ variables: { offeringId, emailType } }).then(() => {
            enqueueSnackbar('Offering emails queued.')
            refetch()
          })
          hideSendConfirmModal()
        }}
      />
    )
  }, [offeringId, sendOfferingEmails, refetch, emailType, enqueueSnackbar])

  const [showCancelModal, hideCancelModal] = useModal(() => {
    return (
      <CancelReminderEmailModal
        hideModal={hideCancelModal}
        onConfirm={() => {
          cancelReminder()
          hideCancelModal()
        }}
      />
    )
  }, [cancelReminder])

  const isEmailReminderDisabled =
    emailType === OfferingEmailType.Feedback ? false : offeringStatus !== 'Live' || offering?.num_shares_remaining === 0
  const actionMenu = (emailType: OfferingEmailType, emailStatus: string) =>
    emailType === OfferingEmailType.Invitation ? (
      <Box px={4} />
    ) : (
      <PopoverAction
        icon={<EllipsisVIcon />}
        actionMenu={
          <Fragment>
            <PopoverListItem button disabled={isEmailReminderDisabled} onClick={showDateTimeModal}>
              <PopoverListItemIcon>
                <PencilAltIcon />
              </PopoverListItemIcon>
              <PopoverListItemText>Schedule</PopoverListItemText>
            </PopoverListItem>
            <PopoverListItem
              button
              disabled={
                emailType === OfferingEmailType.Feedback
                  ? offeringStatus !== 'Expired'
                  : offeringStatus !== 'Live' || offering?.num_shares_remaining === 0
              }
              onClick={showSendConfirmModal}>
              <PopoverListItemIcon>
                <EnvelopeIcon />
              </PopoverListItemIcon>
              <PopoverListItemText>Send Now</PopoverListItemText>
            </PopoverListItem>
            {emailStatus === 'Scheduled' && (
              <PopoverListItem button disabled={isEmailReminderDisabled} onClick={showCancelModal}>
                <PopoverListItemIcon>
                  <CloseIcon />
                </PopoverListItemIcon>
                <PopoverListItemText>Cancel</PopoverListItemText>
              </PopoverListItem>
            )}
          </Fragment>
        }
      />
    )

  const { statusLabel, time } = statusFromTimes(scheduled, sent)

  return (
    <SingleInfoCard
      leftTitle={label}
      rightTitle={statusLabel}
      rightSubtitle={time && moment(time).format('l [at] LT')}
      action={actionMenu(emailType, statusLabel)}
    />
  )
}

export const CampaignScheduleEmailsCard: React.FC<{ offeringId: string }> = ({ offeringId }) => {
  return (
    <Card>
      <CardHeader title="Scheduled Emails" />
      <CardContent>
        {[
          //OfferingEmailType.Invitation,
          OfferingEmailType.Reminder,
          OfferingEmailType.OrderReminder,
          OfferingEmailType.Feedback,
        ].map(emailType => (
          <ReminderToInfoCard key={emailType} offeringId={offeringId} emailType={emailType} />
        ))}
      </CardContent>
    </Card>
  )
}
